import * as React from 'react';
import { useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EndorsementNote from 'components/SubmissionDetails/EndorsementNote';
import { Spacing } from 'components/Layout';
import { useSelector } from 'react-redux';
import {
    getSubmissionDetails,
    getUwBRPropertyValues,
    getUwBRReview,
    getUwReviewsData,
} from 'application/redux/selectors';
import { fetchAdjustmentBRReviewData, postUWReview } from 'services/uwReview';
import {
    AdjustmentBRReviewData,
    AdjustmentReviewBRLocationStateTypes,
    UnderwriterReview,
    UwBRLocationReview,
} from 'types/underwriting.type';
import { currencyToNumber, formatDollar, unformatNumberString } from 'helpers/formatters';
import { makeStyles } from '@mui/styles';
import LrGrid from 'components/LrGrid';
import { defaultColDef, getAdjustmentBRReviewColumnDefs } from './AdjustmentBRReviewColumnDefs';
import { LabeledData } from 'components/Input';
import TextInput from 'components/Input/components/TextInput';
import { currencyMask } from 'helpers/masks';
import { ENDORSEMENT_TYPES, ERRORS, PRODUCT_LINE_TYPES, ROUTES } from '../../../../../../constants';
import { sendAdjustmentReview } from 'services/endorsements';
import ErrorDialog from 'components/ErrorDialog';
import { useActions } from 'hooks/useActions';
import LrDialog from 'components/LrDialog';
import BuildersRiskStandardReview from './BuildersRiskStandardReview';
import LrButton from 'components/LrButton';
import { getReviewLumenParticipationPercent, calculateBrPricingTable } from 'helpers/brCalculationHelper';
import { calculateEndorsementPremium } from 'helpers/calculations';
import * as Actions from 'application/redux/actions';
import { UWReviewTabAPI } from '../StandardReview';
import { AdjustmentReviewRequest } from '../../../../../../types/endorsements.type';
import { calculateTriaPremiumFromRatedPremium } from '../../../../../../helpers/calculations';
import moment from 'moment';
import adjustmentReview from '../CommercialProperty/AdjustmentReview';

interface Props {
    history: any;
}

const isPropertyAutoExtensionLocked = (
    pv: { automaticExtensionRate?: number | null; automaticExtensionPeriod?: string | null },
    effectiveDate: string | undefined,
    expirationDate: string | undefined
): boolean => {
    if (pv.automaticExtensionRate == null) return false;
    const periodParts = (pv.automaticExtensionPeriod ?? '').split(' ');
    const autoExtEnd = moment(effectiveDate).add(
        Number(periodParts[0]),
        periodParts[1] as moment.unitOfTime.DurationConstructor
    );
    return moment(expirationDate).isSameOrBefore(autoExtEnd);
};

interface LocationPremiums {
    proratedPremium: string;
    triaPremium: string;
    totalPremium: string;
}

const MY_PRODUCT_LINE_TYPE = PRODUCT_LINE_TYPES.BUILDERS_RISK;

// User-editable rate modifier fields in the UW review form.
// Auto-save on mount only updates derived fields (finalRate, programPremium, etc.), never these inputs.
const REVIEW_EDIT_FIELDS = [
    'baseRate',
    'modeled',
    'additionalCatExposure',
    'renovationModifier',
    'midTermPlacement',
    'deductibleStructure',
    'contractorQuality',
    'siteSecurity',
    'leadCarrier',
    'constructionModifier',
    'underwriterAdjustedPremium',
];

const useStyles = makeStyles({
    summaryCard: {
        marginBottom: '2rem',
    },
    fieldLabel: {
        fontWeight: 500,
        marginBottom: '0.5rem',
    },
    gridCard: {
        marginBottom: '2rem',
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginTop: '2rem',
    },
});

const AdjustmentBRReview: React.FunctionComponent<Props> = (props) => {
    const { history } = props;
    const classes = useStyles();

    // Global State
    const submission = useSelector(getSubmissionDetails);
    const propertyValues = useSelector(getUwBRPropertyValues);
    const { uwReviewSaveBRReview, uwReviewSaveAllBRProperties } = useActions(Actions);
    const uwReviews = useSelector(getUwReviewsData);
    const uwReviewCurrent = useSelector(getUwBRReview);

    // Local State
    const [adjustmentReviewData, setAdjustmentReviewData] = React.useState<AdjustmentBRReviewData>(
        {} as AdjustmentBRReviewData
    );
    const [narrative, setNarrative] = React.useState<string>('');
    const [rowData, setRowData] = React.useState<any[]>([]);
    const [locationPremiums, setLocationPremiums] = React.useState<{ [key: number]: LocationPremiums }>({});
    const [apiError, setApiError] = React.useState<boolean>(false);
    const [isReviewDialogOpen, setIsReviewDialogOpen] = React.useState<boolean>(false);
    const [selectedUnderwriterReview, setSelectedUnderwriterReview] = React.useState<UnderwriterReview | undefined>();
    const [reviewPayload, setReviewPayload] = React.useState<any>(null);
    const uwReviewTabAPI = React.useRef<UWReviewTabAPI>({} as UWReviewTabAPI);
    const [hasManualPremiumChanges, setHasManualPremiumChanges] = React.useState<boolean>(false);
    const [reviewSnapshot, setReviewSnapshot] = React.useState<any>(null);
    const [usingAutoExtensionRate, setUsingAutoExtensionRate] = React.useState<boolean>(false);
    // Tracks whether the user has made real changes in the UW review dialog (vs auto-save on mount).
    // Using a ref so reads inside setSuggestedPremiums are always current (avoids stale closure).
    const userHasEditedReviewRef = React.useRef<boolean>(false);
    // Snapshot of propertyValues taken when the review dialog opens, used to detect real user edits at close time.
    const propertyValuesAtOpenRef = React.useRef<any[] | null>(null);

    const readOnly = submission.isBound;

    // Lock the entire review (except narrative) when within the automatic extension period
    const isAutoExtensionLocked =
        submission?.endorsementType?.code === ENDORSEMENT_TYPES.EXTENSION.valueOf() &&
        propertyValues?.some((pv) =>
            isPropertyAutoExtensionLocked(pv, submission.effectiveDate, submission.expirationDate)
        );

    // Helper function to build row data for AG Grid from location data
    const buildRowData = (data: AdjustmentBRReviewData, showAutoRateMarker: boolean = false) => {
        const rows: any[] = [];
        data.locationData.forEach((location, index) => {
            const locationId = `location-${index}`;

            // Add location header row
            rows.push({
                id: locationId,
                locationName: location.locationName || `Location ${index + 1}`,
                origValue: ' ',
                currentValue: ' ',
                updatedValue: readOnly ? undefined : ' ',
                isGroup: true,
                groupId: locationId,
                orgHierarchy: [locationId],
            });

            const originalState = location.locationStates[AdjustmentReviewBRLocationStateTypes.ORIGINAL.valueOf()]!;
            const currentState = location.locationStates[AdjustmentReviewBRLocationStateTypes.CURRENT.valueOf()]!;
            const updatedState = location.locationStates[AdjustmentReviewBRLocationStateTypes.UPDATED.valueOf()]!;
            // Add detail rows for this location
            const detailRows = [
                {
                    label: 'Hard Costs',
                    origValue: formatDollar(originalState.hardCosts, 0),
                    currentValue: formatDollar(currentState.hardCosts, 0),
                    updatedValue: formatDollar(updatedState.hardCosts, 0),
                },
                {
                    label: 'Soft Costs',
                    origValue: formatDollar(originalState.softCosts, 0),
                    currentValue: formatDollar(currentState.softCosts, 0),
                    updatedValue: formatDollar(updatedState.softCosts, 0),
                },
                {
                    label: 'Loss of Gross Earnings',
                    origValue: formatDollar(originalState.lossOfGrossEarnings, 0),
                    currentValue: formatDollar(currentState.lossOfGrossEarnings, 0),
                    updatedValue: formatDollar(updatedState.lossOfGrossEarnings, 0),
                },
                {
                    label: 'Loss of Rental Income',
                    origValue: formatDollar(originalState.lossOfRentalIncome, 0),
                    currentValue: formatDollar(currentState.lossOfRentalIncome, 0),
                    updatedValue: formatDollar(updatedState.lossOfRentalIncome, 0),
                },
                {
                    label: 'TCV',
                    origValue: formatDollar(originalState.tcv, 0),
                    currentValue: formatDollar(currentState.tcv, 0),
                    updatedValue: formatDollar(updatedState.tcv, 0),
                },
                {
                    label: 'Final Hard Cost Rate',
                    origValue: formatDollar(originalState.finalRate, 3),
                    currentValue: formatDollar(currentState.finalRate, 3),
                    updatedValue: `${formatDollar(updatedState.finalRate, 3)}${showAutoRateMarker ? '*' : ''}`,
                },
                {
                    label: 'Lumen Charged Premium',
                    origValue: formatDollar(originalState.chargedPremium, 0),
                    currentValue: formatDollar(currentState.chargedPremium, 0),
                    updatedValue: `${formatDollar(updatedState.chargedPremium, 0)}**`,
                },
            ];

            detailRows.forEach((detail, detailIndex) => {
                rows.push({
                    id: `${locationId}-detail-${detailIndex}`,
                    locationName: detail.label,
                    origValue: detail.origValue,
                    currentValue: detail.currentValue,
                    updatedValue: readOnly ? undefined : detail.updatedValue,
                    isGroup: false,
                    groupId: locationId,
                    orgHierarchy: [locationId, detail.label],
                });
            });
        });
        return rows;
    };

    const fetchData = async () => {
        if (submission?.id) {
            const adjustmentData = await fetchAdjustmentBRReviewData(submission.id);
            if (adjustmentData) {
                setAdjustmentReviewData(adjustmentData);
                setRowData(buildRowData(adjustmentData));
                setNarrative(adjustmentData.narrative || '');
                if (adjustmentData.underwriterReview) {
                    setSelectedUnderwriterReview(adjustmentData.underwriterReview);
                }
            }
        }
    };

    const setSuggestedPremiums = () => {
        if (!propertyValues || propertyValues.length === 0 || !(adjustmentReviewData?.locationData?.length > 0)) return;

        // If there are manual premium changes, and the user has not since made changes to the review, keep the manual values.
        if (hasManualPremiumChanges && reviewSnapshot && JSON.stringify(uwReviews) === JSON.stringify(reviewSnapshot))
            return;

        setHasManualPremiumChanges(false);

        const isExtensionEndorsement = submission.endorsementType?.code === ENDORSEMENT_TYPES.EXTENSION.valueOf();
        const participationStr = getReviewLumenParticipationPercent(
            uwReviewCurrent?.policyLimitOfLiability ?? 0,
            uwReviewCurrent?.lumenLimitOfLiability ?? 0
        );
        const participation = Number(participationStr);

        // Use the automatic extension rate only if the user hasn't explicitly edited the UW review.
        // Read directly from the ref so this is always current (avoids stale closure from React state).
        const useAutomaticExtensionRate = isExtensionEndorsement && !userHasEditedReviewRef.current;

        let newLocationPremiums: { [key: number]: LocationPremiums } = {};
        adjustmentReviewData.locationData.forEach((ld) => {
            const pv = propertyValues.find((p) => p.locationId?.toString() === ld.locationId?.toString());
            if (!pv) return;

            let proratedPremiumChange: number;

            // If the submission is in force, that means the review has been completed. We don't need to recalculate suggested premiums.
            // The premium stored on the review is the premium assigned by the underwriter. So just display that.
            if (readOnly) proratedPremiumChange = pv.totalPremium || 0;
            else if (isExtensionEndorsement) {
                // For extension endorsements, prorate the current program premium over the extension period.
                const updatedState = ld.locationStates[AdjustmentReviewBRLocationStateTypes.UPDATED];
                const origState = ld.locationStates[AdjustmentReviewBRLocationStateTypes.ORIGINAL];
                let programPremium = updatedState?.programPremium ?? origState?.programPremium ?? 0;

                // If an automatic extension rate is set and the user hasn't changed the review,
                // recalculate programPremium from the extension rate
                if (useAutomaticExtensionRate && pv.automaticExtensionRate != null) {
                    const extPricingTable = calculateBrPricingTable(
                        updatedState as any,
                        pv as any,
                        {
                            effectiveDate: adjustmentReviewData.origEffectiveDate,
                            expirationDate: adjustmentReviewData.origExpirationDate,
                        } as any,
                        pv.automaticExtensionRate
                    );
                    programPremium = extPricingTable.premiumSubtotal || 0;
                } else if (!useAutomaticExtensionRate) {
                    // User has edited the review — recalculate from the current pv values (which
                    // have the updated finalRate from the UW review form).
                    const editedPricingTable = calculateBrPricingTable(
                        updatedState as any,
                        pv as any,
                        {
                            effectiveDate: adjustmentReviewData.origEffectiveDate,
                            expirationDate: adjustmentReviewData.origExpirationDate,
                        } as any
                    );
                    programPremium = editedPricingTable.premiumSubtotal || 0;
                }

                proratedPremiumChange =
                    (calculateEndorsementPremium(
                        programPremium,
                        adjustmentReviewData.origEffectiveDate,
                        adjustmentReviewData.origExpirationDate,
                        adjustmentReviewData.updatedEffectiveDate,
                        adjustmentReviewData.updatedExpirationDate
                    ) *
                        participation) /
                    100;
            } else {
                const previousPremium =
                    ld.locationStates[AdjustmentReviewBRLocationStateTypes.CURRENT].programPremium ??
                    ld.locationStates[AdjustmentReviewBRLocationStateTypes.ORIGINAL].programPremium;
                const updatedState = ld.locationStates[AdjustmentReviewBRLocationStateTypes.UPDATED];

                // Recalculate the new program premium using the original policy dates so that both new and old
                // premiums are computed over the same full-term period.
                const pricingTable = calculateBrPricingTable(
                    updatedState as any,
                    pv as any,
                    {
                        effectiveDate: adjustmentReviewData.origEffectiveDate,
                        expirationDate: adjustmentReviewData.origExpirationDate,
                    } as any
                );
                const newFullTermProgramPremium = pricingTable.premiumSubtotal || 0;
                proratedPremiumChange = ((newFullTermProgramPremium - previousPremium) * participation) / 100;
            }

            const triaPremium = calculateTriaPremiumFromRatedPremium(
                proratedPremiumChange,
                ld.propertyReview?.triaPercentage || 0
            );
            newLocationPremiums = {
                ...newLocationPremiums,
                [pv.locationId!]: {
                    proratedPremium: formatDollar(proratedPremiumChange, 0),
                    triaPremium: formatDollar(triaPremium, 0),
                    totalPremium: formatDollar(proratedPremiumChange + triaPremium, 0),
                },
            };

            ld.locationStates[AdjustmentReviewBRLocationStateTypes.UPDATED].chargedPremium = proratedPremiumChange;
            ld.locationStates[AdjustmentReviewBRLocationStateTypes.UPDATED].finalRate =
                useAutomaticExtensionRate && pv.automaticExtensionRate != null
                    ? pv.automaticExtensionRate
                    : pv.finalRate;
        });
        setLocationPremiums(newLocationPremiums);
        setUsingAutoExtensionRate(useAutomaticExtensionRate);
        setRowData(buildRowData(adjustmentReviewData, useAutomaticExtensionRate));
    };

    const onGridReady = (params: any) => {
        // Expand all groups by default
        params.api.forEachNode((node: any) => {
            if (node.group) {
                node.setExpanded(true);
            }
        });
    };

    useEffect(() => {
        fetchData();
    }, [submission]);

    useEffect(() => {
        if (adjustmentReviewData.locationData) {
            setRowData(buildRowData(adjustmentReviewData));
        }
    }, [adjustmentReviewData]);

    useEffect(() => {
        setSuggestedPremiums();
    }, [adjustmentReviewData, uwReviews, uwReviewCurrent]);

    useEffect(() => {
        const uwReview = uwReviews.find(
            (ur) =>
                ur.underwriterReview?.submissionId?.toString() === submission.id &&
                ur.underwriterReview?.productLineTypeCode === MY_PRODUCT_LINE_TYPE
        );
        const locationReviews: UwBRLocationReview[] = uwReview?.locationReviews || [];
        uwReview && uwReviewSaveBRReview(uwReview.underwriterReview);
        uwReview?.locationReviews && uwReviewSaveAllBRProperties(locationReviews);
    }, [uwReviews, adjustmentReviewData]);

    // Handle location premium change
    const handleLocationPremiumChange = (locationId: string, value: string) => {
        setHasManualPremiumChanges(true);
        const id = Number(locationId);
        const proratedPremium = Number(unformatNumberString(value));
        const triaPercentage = propertyValues.length
            ? propertyValues.find((pv) => pv.locationId?.toString() === locationId.toString())?.triaPercentage || 0
            : adjustmentReviewData?.locationData?.find((ld) => ld.locationId === locationId)?.propertyReview
                  ?.triaPercentage ?? 0;
        const triaPremium = calculateTriaPremiumFromRatedPremium(proratedPremium, triaPercentage);
        const totalPremium = proratedPremium + triaPremium;
        const newLocationPremiums = {
            ...locationPremiums,
            [id]: {
                proratedPremium: formatDollar(proratedPremium, 0),
                triaPremium: formatDollar(triaPremium, 0),
                totalPremium: formatDollar(totalPremium, 0),
            },
        };
        setLocationPremiums(newLocationPremiums);

        // Update Redux propertyValues with manual override so it persists in the UW review payload
        const numericValue = Number(currencyToNumber(value || '0'));
        const updatedPropertyValues = propertyValues.map((pv) => {
            if (pv.locationId?.toString() === locationId.toString()) {
                return {
                    ...pv,
                    totalPremium: numericValue,
                    // Null out the individual line premiums to indicate manual override
                    hardCostPremium: undefined,
                    softCostPremium: undefined,
                    lossOfGrossEarningsPremium: undefined,
                    lossOfRentalIncomePremium: undefined,
                };
            }
            return pv;
        });
        uwReviewSaveAllBRProperties(updatedPropertyValues);

        // Note: We intentionally do NOT update adjustmentReviewData.locationStates here.
        // The grid should always display the calculated value from the UW review,
        // not the manual override. Manual overrides only appear in the premium input field.
    };

    const onSubmit = async () => {
        try {
            const premiums = Object.fromEntries(
                Object.entries(locationPremiums).map(([key, value]) => [
                    key,
                    currencyToNumber(value.totalPremium || '0'),
                ])
            );

            const propertyReviews = {};
            propertyValues.forEach(
                (pv) =>
                    (propertyReviews[pv.locationId!] = { ...pv, productLineTypeCode: PRODUCT_LINE_TYPES.BUILDERS_RISK })
            );

            const payload: AdjustmentReviewRequest = {
                // If user opened the underwriter review via cog, reviewPayload will be populated from it.
                // Build off that to capture any updates.
                // But we want to overwrite the premium and narrative fields because premium will be calculated proRata
                // from this view and narrative is a new entry.
                underwriterReview: {
                    ...(reviewPayload ?? {}),
                    id: adjustmentReviewData.underwriterReview?.id,
                    premium:
                        Object.values(locationPremiums).reduce((sum: number, prem: LocationPremiums) => {
                            return sum + Number(currencyToNumber(prem.totalPremium || '0'));
                        }, 0) || 0,
                    policyFee: 0,
                    professionalFee: 0,
                    slaFee: 0,
                    slaTax: 0,
                },
                premiums: premiums,
                narrative: narrative,
                propertyReviews: propertyReviews,
            };

            await sendAdjustmentReview(submission.id, payload);
            history.push(ROUTES.submissions());
        } catch (e) {
            setApiError(true);
        }
    };

    const handleOpenReviewDialog = () => {
        // Save off snapshot of reviews. This will be used to determine if we've made changes to the review in the dialog.
        setReviewSnapshot(JSON.parse(JSON.stringify(uwReviews)));
        // Capture current propertyValues so we can detect real user edits when the dialog closes.
        // We compare only the user-editable rate modifier fields (auto-save on mount never changes these).
        propertyValuesAtOpenRef.current = JSON.parse(JSON.stringify(propertyValues ?? []));

        setIsReviewDialogOpen(true);
    };

    const handleCloseReviewDialog = () => {
        // Detect if the user actually changed any rate modifier inputs by comparing to the snapshot captured at open.
        // Auto-save on form mount only updates derived fields (finalRate, programPremium, etc.), never the input fields.
        if (propertyValuesAtOpenRef.current && propertyValues) {
            const userEdited = propertyValues.some((pv) => {
                const snap = propertyValuesAtOpenRef.current!.find(
                    (s: any) => s.locationId?.toString() === pv.locationId?.toString()
                );
                if (!snap) return false;
                return REVIEW_EDIT_FIELDS.some((field) => (pv as any)[field] !== (snap as any)[field]);
            });
            if (userEdited) {
                userHasEditedReviewRef.current = true;
            }
        }
        // Capture the latest review payload for use when submitting the adjustment review.
        setReviewPayload(uwReviewTabAPI.current.buildReviewPayload());
        // Recompute suggested premiums so UW changes override any manual entries for affected fields
        setSuggestedPremiums();
        setIsReviewDialogOpen(false);
    };

    return (
        <div style={{ marginLeft: '1rem', marginRight: '1rem', marginTop: '3rem' }}>
            <EndorsementNote submissionId={submission.endorsingSubmissionId} />
            <Spacing height="2rem" />

            {/* Summary Card */}
            <Card className={classes.summaryCard}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={4}>
                            <LabeledData
                                title="ProRata Premium Change"
                                value={formatDollar(
                                    Object.values(locationPremiums).reduce((sum: number, prem: LocationPremiums) => {
                                        return sum + Number(currencyToNumber(prem.proratedPremium || '0'));
                                    }, 0) || 0,
                                    0
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <LabeledData
                                title="Effective Date"
                                value={adjustmentReviewData.updatedEffectiveDate || '-'}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <LabeledData
                                title="Expiration Date"
                                value={
                                    (submission.endorsementType?.code === 'EXTENSION'
                                        ? adjustmentReviewData.updatedExpirationDate
                                        : adjustmentReviewData.origExpirationDate) || '-'
                                }
                            />
                        </Grid>
                    </Grid>

                    <Spacing height="2rem" />

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabeledData title="Description" value={submission.narrative} />
                        </Grid>
                    </Grid>

                    <Spacing height="2rem" />

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabeledData title="Narrative" value=" " />
                            <TextField
                                data-testid="narrative-textfield"
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                value={narrative}
                                onChange={(e) => setNarrative(e.target.value)}
                                placeholder="Enter narrative"
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Location Premium Table */}
            {adjustmentReviewData.locationData && adjustmentReviewData.locationData.length > 0 && (
                <Card className={classes.summaryCard}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" gutterBottom>
                                Endorsement Premium Adjustments
                            </Typography>
                            {!readOnly && (
                                <IconButton
                                    onClick={handleOpenReviewDialog}
                                    size="small"
                                    title="Edit Underwriter Review"
                                    color="primary"
                                >
                                    <SettingsIcon />
                                </IconButton>
                            )}
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Location</TableCell>
                                    <TableCell>ProRata Premium Change</TableCell>
                                    <TableCell>TRIA Premium Change</TableCell>
                                    <TableCell>Total Premium Change</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {adjustmentReviewData.locationData.map((location) => (
                                    <TableRow key={location.locationId}>
                                        <TableCell>
                                            <Typography variant="body1" style={{ marginBottom: '0' }}>
                                                {location.locationName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <TextInput
                                                variant="underlined"
                                                value={locationPremiums[location.locationId]?.proratedPremium || ''}
                                                onChange={(event) =>
                                                    handleLocationPremiumChange(location.locationId, event.target.value)
                                                }
                                                mask={currencyMask}
                                                style={{ width: '150px' }}
                                                readOnly={readOnly || isAutoExtensionLocked}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1">
                                                {locationPremiums[location.locationId]?.triaPremium || ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1">
                                                {locationPremiums[location.locationId]?.totalPremium || ''}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {hasManualPremiumChanges && (
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>
                                            <Typography variant="caption">
                                                Note: premiums have been manually adjusted
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                    {isAutoExtensionLocked && (
                        <Typography variant="caption" style={{ marginLeft: '1rem' }}>
                            Automatic extension - premium change disabled
                        </Typography>
                    )}
                </Card>
            )}

            {/* Locations Grid */}
            <Card className={classes.gridCard}>
                <CardContent style={{ padding: 0 }}>
                    <LrGrid
                        columnDefs={getAdjustmentBRReviewColumnDefs(
                            adjustmentReviewData.origEffectiveDate,
                            adjustmentReviewData.currentEffectiveDate,
                            adjustmentReviewData.updatedEffectiveDate,
                            readOnly
                        )}
                        defaultColDef={defaultColDef}
                        onGridReady={onGridReady}
                        rowData={rowData}
                        treeData={true}
                        animateRows={true}
                        groupDefaultExpanded={1}
                        getDataPath={(data: any) => data.orgHierarchy}
                        autoGroupColumnDef={{
                            headerName: 'Location',
                            minWidth: 300,
                            cellRendererParams: {
                                suppressCount: true,
                            },
                            valueGetter: (params: any) => {
                                // Display the locationName instead of the hierarchy path
                                return params.data?.locationName;
                            },
                        }}
                        domLayout="autoHeight"
                        height=""
                        getRowId={(params) => String(params.data.id)}
                        hideStatusBar={true}
                    />
                    {usingAutoExtensionRate && (
                        <Typography variant="caption" style={{ display: 'block', marginTop: '1rem' }}>
                            * Automatic extension rate applied. Open the gear icon to override with a custom rate.
                        </Typography>
                    )}
                    <Typography variant="caption">
                        ** Suggested premium based on current review data. Edit review via gear icon to recalculate
                        suggested premium.
                    </Typography>
                </CardContent>
            </Card>

            {/* Action Buttons */}
            {!readOnly && (
                <Box className={classes.actionButtons}>
                    <Button variant="text" color="secondary" size="large">
                        Decline
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={onSubmit}
                        disabled={!narrative.trim()}
                    >
                        Submit
                    </Button>
                </Box>
            )}

            {/* LEGACY: Do not use this pattern. Use the error context (useErrorDialog) instead. */}
            {/* See examples in `src/views/SubmissionDetails/index.tsx` and `src/views/SubmissionDetails/components/QuotesBind/index.tsx` */}
            {apiError && (
                <ErrorDialog
                    errorTitle={ERRORS.API_ERROR_MSG_TITLE}
                    errorMsg={ERRORS.API_ERROR_MSG_BODY}
                    onClose={() => setApiError(false)}
                />
            )}

            {/* Review Dialog */}
            <LrDialog
                isOpen={isReviewDialogOpen}
                onCancel={handleCloseReviewDialog}
                title="Underwriter Review"
                fullWidth
            >
                <BuildersRiskStandardReview
                    history={history}
                    readOnly={readOnly || isAutoExtensionLocked}
                    selectedUnderwriterReviews={[uwReviewCurrent]}
                    ref={(ref) => {
                        if (ref) uwReviewTabAPI.current = ref;
                    }}
                    // saveDraftButtonText="Save and Close"
                    // onSave={handleReviewDialogUpdate}
                    showSaveDraftButton={false}
                />
                <LrButton
                    variant="text"
                    title="Cancel"
                    data-testid="cancel-review-dialog-button"
                    onClick={handleCloseReviewDialog}
                    style={{ marginTop: '0', marginBottom: '0' }}
                >
                    Close
                </LrButton>
            </LrDialog>
        </div>
    );
};

export default AdjustmentBRReview;
